import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AdminTabs } from "../components/admin/AdminTabs";

const TONE = { overdue: "border-red-300 bg-red-50", "due today": "border-amber-300 bg-amber-50", "to fit": "border-sky-300 bg-sky-50", out: "border-stone-200 bg-white" };
const due = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
const hm = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

const status = (m) => (m.state === "to fit" ? `To fit today at ${hm(m.fitted_at)}` : m.state === "overdue" ? `Overdue - was due ${due(m.return_at)}` : `Due back ${due(m.return_at)}${m.fitted_marked ? ` · fitted ${hm(m.fitted_at)}` : ""}`);

const Card = ({ m, onReturn, onFit }) => {
  const fit = m.state === "to fit";
  return (
    <div data-testid={`desk-card-${m.id}-${m.kind}`} className={`rounded-lg border-2 p-5 flex items-center gap-4 ${TONE[m.state]}`}>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-[0.14em] text-ink-muted">{m.label} · {m.booking_ref}</div>
        <div className="font-serif text-2xl truncate">{m.patient_name || "Patient"}</div>
        <div className={`text-sm ${m.state === "overdue" ? "text-red-700 font-medium" : "text-ink-muted"}`}>{status(m)}</div>
      </div>
      <button type="button" onClick={() => (fit ? onFit(m) : onReturn(m))} data-testid={`desk-${fit ? "fit" : "return"}-${m.id}-${m.kind}`}
        className={`shrink-0 rounded-full text-white text-lg px-8 py-5 active:scale-95 transition-transform ${fit ? "bg-sky-700" : "bg-bronze-dark"}`}>{fit ? "Fitted" : "Returned"}</button>
    </div>
  );
};

export default function AdminReturns() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback((quiet) => {
    api.get("/admin/devices").then(({ data }) => setRows(data.monitors.filter((m) => m.state !== "returned")))
      .catch((e) => { if (!quiet) toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); });
  }, []);
  useEffect(() => { load(); const t = setInterval(() => load(true), 60000); return () => clearInterval(t); }, [load]);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const post = (m, returned) => api.post(`/admin/devices/returns/${encodeURIComponent(m.id)}/${m.kind}`, { returned });
  const markReturned = (m) => post(m, true).then(() => {
    setRows((r) => r.filter((x) => !(x.id === m.id && x.kind === m.kind)));
    toast.success(`${m.label} from ${m.patient_name || m.booking_ref} checked in.`, { action: { label: "Undo", onClick: () => post(m, false).then(() => load()).catch(err) } });
  }).catch(err);
  const markFitted = (m) => api.post(`/admin/devices/fitted/${encodeURIComponent(m.id)}/${m.kind}`, { fitted: true }).then(() => {
    load();
    toast.success(`${m.label} fitted for ${m.patient_name || m.booking_ref} - return time set from now.`,
      { action: { label: "Undo", onClick: () => api.post(`/admin/devices/fitted/${encodeURIComponent(m.id)}/${m.kind}`, { fitted: false }).then(() => load()).catch(err) } });
  }).catch(err);
  const shown = useMemo(() => (rows || []).filter((m) => `${m.patient_name} ${m.booking_ref}`.toLowerCase().includes(q.trim().toLowerCase())), [rows, q]);
  return (
    <div className="container-ccc py-10 max-w-4xl" data-testid="admin-returns-page">
      <h1 className="font-serif text-4xl">Monitor returns</h1>
      <AdminTabs active="/admin/returns" />
      <p className="mt-4 text-ink-muted">Tap <b>Fitted</b> when a monitor goes on - the return time is set from that moment. When a patient hands it back, tap <b>Returned</b>: their reminders stop and the monitor is free to book again.</p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient name or booking ref" data-testid="desk-search"
        className="mt-6 w-full rounded-lg border-2 border-stone-300 bg-white px-4 py-4 text-lg" />
      <div className="mt-6 space-y-3">
        {rows === null && <p className="text-ink-muted">Loading…</p>}
        {rows && shown.length === 0 && <p data-testid="desk-empty" className="text-lg text-ink-muted p-6 bg-cream-100 rounded-lg">{q ? "No match - check the spelling or booking ref." : "No monitors are out right now."}</p>}
        {shown.map((m) => <Card key={`${m.id}-${m.kind}`} m={m} onReturn={markReturned} onFit={markFitted} />)}
      </div>
    </div>
  );
}
